import * as Immutable from 'immutable';
import * as React from 'react';

import { localeData } from '../../locales/data';
import { addModifiers } from '../../shared/helpers';
import { StringTMap } from '../../types';
import { FormattedMessageHelper } from '../../shared/helpers/internationalization';
import analytics from '../../shared/services/analytics/analytics';
import {
  AgreementsButtons,
  AgreementsButtonsProps,
  AgreementsList,
  displayAgreementContent,
  useIsReadAllContent,
} from './AgreementsHelpers';

interface AgreementsProps {
  banner?: string;
  modifiers?: string[];
  info?: () => JSX.Element | JSX.Element[];
  agreements: Immutable.Map<string, any>;
  handleClose?: () => void;
  handleLater?: () => void;
  handleAccepted?: () => void;
  handleReject?: () => void;
  agreementsListValues?: StringTMap<boolean>;
  agreementsList?: Object[];
  handleChangeAgreements: (event: any, data: any) => void;
  agreementsAccepted?: boolean;
  isAgreementButtonVisible: boolean;
  isEnrollModal?: boolean;
  competitionName?: string;
  competitionId?: string;
  buttonAcceptedText?: string;
  buttonCloseText?: string;
}

export const AgreementsButtonsCompetitions = ({ handleAccepted, handleReject, handleLater, competitionId }: AgreementsButtonsProps) => {
  const gaPayload = competitionId ? { event_label: competitionId } : undefined;

  return (
    <div className="agreements__buttons">
      <div className="agreements__buttons--left">
        {handleReject && (
          <button
            type="button"
            className="link agreements__reject-link"
            onClick={() => {
              analytics.trackCustomEvent('competition_modal_click_not_interested', gaPayload);
              handleReject();
            }}
          >
            <FormattedMessageHelper
              tagName="span"
              id="agreements.notInterested"
              defaultMessage={localeData.en['agreements.notInterested']}
            />
          </button>
        )}
      </div>

      <div className="modal__section--buttons modal__section--buttons--right">
        {handleLater && (
          <button
            onClick={() => {
              analytics.trackCustomEvent('competition_modal_click_not_now', gaPayload);
              handleLater();
            }}
            className="button button--secondary"
          >
            <FormattedMessageHelper tagName="span" id="agreements.notNow" defaultMessage={localeData.en['agreements.notNow']} />
          </button>
        )}
        {handleAccepted && (
          <button
            onClick={() => {
              analytics.trackCustomEvent('competition_modal_click_agree_and_enroll', gaPayload);
              handleAccepted();
            }}
            className="button"
          >
            <FormattedMessageHelper
              tagName="span"
              id="agreements.iAgreeAndEnroll"
              defaultMessage={localeData.en['agreements.iAgreeAndEnroll']}
            />
          </button>
        )}
      </div>
    </div>
  );
};

const agreementsButtons = new Map<string, string>([
  [
    'biometrics-consent-agreement-kr-1',
    'I have fully understood the Company’s explanation on mandatory collection and use of general personal information and hereby consent thereto.',
  ],
  [
    'biometrics-consent-agreement-kr-2',
    'I have fully understood the Company’s explanation on mandatory collection and use of sensitive information and hereby consent thereto.',
  ],
  [
    'biometrics-consent-agreement-kr-3',
    'I have fully understood the Company’s explanation on mandatory collection and use of unique identification information and hereby consent thereto.',
  ],
  [
    'biometrics-consent-agreement-kr-4',
    'I have fully understood the Company’s explanation on transfer of general personal information to a third party and hereby consent thereto.',
  ],
  [
    'biometrics-consent-agreement-kr-5',
    'I have fully understood the Company’s explanation on transfer of sensitive information to a third party and hereby consent thereto.',
  ],
  [
    'biometrics-consent-agreement-kr-6',
    'I have fully understood the Company’s explanation on transfer of unique identification information to a third party and hereby consent thereto.',
  ],
  [
    'biometrics-consent-agreement-kr-7',
    'I have fully understood the Company’s explanation on overseas transfer of general personal information and hereby consent thereto.',
  ],
  [
    'biometrics-consent-agreement-kr-8',
    'I have fully understood the Company’s explanation on overseas transfer of sensitive information and hereby consent thereto.',
  ],
  [
    'biometrics-consent-agreement-kr-9',
    'I have fully understood the Company’s explanation on overseas transfer of unique identification information and hereby consent thereto.',
  ],
]);

const RejectableAgreementsButtons = ({
  handleAccepted,
  handleReject,
  handleLater,
  isAgreementButtonVisible,
  agreementsAccepted,
  handleClose,
  buttonAcceptedText = 'agreements.iAgreeToRejectable',
  buttonCloseText = 'agreements.close',
  agreementId,
}: AgreementsButtonsProps) => {
  let acceptedButtonTextElement = <FormattedMessageHelper tagName="span" id={buttonAcceptedText} />;
  if (agreementId && agreementsButtons.has(agreementId)) {
    acceptedButtonTextElement = <span>{agreementsButtons.get(agreementId)}</span>;
  }
  return (
    <div className="agreements__buttons">
      {isAgreementButtonVisible && (
        <button
          data-testid={'agreements-accept-button'}
          type={'submit'}
          disabled={!agreementsAccepted}
          onClick={handleAccepted}
          className={addModifiers(
            'button',
            !agreementsAccepted ? ['disabled', 'secondary', 'rejectable-main'] : ['secondary', 'rejectable-main']
          )}
        >
          {acceptedButtonTextElement}
        </button>
      )}
      {handleReject && (
        <button onClick={handleReject} className="button button--secondary">
          <FormattedMessageHelper tagName="span" id="agreements.reject" />
        </button>
      )}
      {handleClose && (
        <button type={'button'} onClick={handleClose} className="button button--secondary">
          <FormattedMessageHelper tagName="span" id={buttonCloseText} />
        </button>
      )}
    </div>
  );
};

export function getIsRejectable(agreement: Immutable.Map<string, any>): boolean {
  const rejectableAgreementPrefix = 'biometrics-consent-agreement';
  return !!(agreement && agreement.get('id') && agreement.get('id').startsWith(rejectableAgreementPrefix));
}

function formatAgreementTitle(title: string) {
  const lowerCaseTitle = title.toLowerCase();
  const bracketIndex = lowerCaseTitle.indexOf('(');
  if (bracketIndex !== -1) {
    return lowerCaseTitle.substring(0, bracketIndex).trim();
  }
  return lowerCaseTitle;
}

export const AgreementsMain = ({
  banner,
  info,
  agreements,
  handleAccepted,
  handleReject,
  handleClose,
  agreementsListValues,
  agreementsList,
  handleChangeAgreements,
  agreementsAccepted,
  isAgreementButtonVisible,
  modifiers = [],
  isEnrollModal,
  competitionName,
  competitionId,
  buttonAcceptedText,
  buttonCloseText,
  handleLater,
}: AgreementsProps) => {
  const contentRef = React.useRef<HTMLDivElement | null>(null);

  const { isReadAllContent } = useIsReadAllContent(contentRef);
  const isRejectable = getIsRejectable(agreements);

  function getButtons() {
    if (isRejectable) {
      const closeText = handleReject || !isAgreementButtonVisible ? 'agreements.close' : 'agreements.reject';
      return (
        <RejectableAgreementsButtons
          handleReject={handleReject}
          handleAccepted={handleAccepted}
          handleLater={handleLater}
          handleClose={handleClose}
          buttonAcceptedText={buttonAcceptedText}
          buttonCloseText={closeText}
          isAgreementButtonVisible={isAgreementButtonVisible}
          agreementsAccepted={isReadAllContent}
          agreementId={agreements.get('id')}
        />
      );
    }
    if (isEnrollModal) {
      return (
        <AgreementsButtonsCompetitions
          handleClose={handleClose}
          handleReject={handleReject}
          handleAccepted={handleAccepted}
          handleLater={handleLater}
          competitionId={competitionId}
        />
      );
    }
    return (
      <AgreementsButtons
        handleAccepted={handleAccepted}
        isAgreementButtonVisible={isAgreementButtonVisible}
        agreementsAccepted={agreementsAccepted}
        handleClose={handleClose}
        buttonAcceptedText={buttonAcceptedText}
        buttonCloseText={buttonCloseText}
        handleLater={handleLater}
      />
    );
  }

  const title = agreements?.get('title');
  const instruction =
    !competitionName && title
      ? `Please read the following ${formatAgreementTitle(title)} carefully and scroll down to the end.`
      : 'Please read the following agreement carefully and scroll down to the end.';
  return (
    <React.Fragment>
      {agreements && agreements.get('title') && (
        <header className="agreements__header">
          <h3 className="agreements__title">
            {/* Even isEnrollModal is true, if the competitionName is not available, I think it is better to show the agreement's title */}
            {isEnrollModal && competitionName ? (
              <FormattedMessageHelper id="agreements.enroll.today" values={{ competitionName }} />
            ) : (
              agreements.get('title')
            )}
          </h3>
        </header>
      )}
      <div className={addModifiers('agreements', modifiers)}>
        {info && <div className="agreements__info">{info()}</div>}
        {banner && <img className="agreements__banner" src={banner} alt={agreements && `Banner of ${agreements.get('title')}`} />}
        {((agreements && isEnrollModal) || (agreements && !isEnrollModal && isAgreementButtonVisible)) && (
          <div className="agreements__instruction">
            <p>{instruction}</p>
          </div>
        )}
        <div className="agreements__content" ref={contentRef}>
          <div className="agreements__content-scroll">
            <div className="agreements__content-text">
              {agreements ? (
                displayAgreementContent(agreements)
              ) : (
                <div className="agreements__not-available">
                  <FormattedMessageHelper tagName="span" id="agreements.available" defaultMessage={localeData.en['agreements.available']} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {isAgreementButtonVisible && agreementsList && agreementsListValues && !isEnrollModal && !isRejectable && (
        <AgreementsList
          isValidToAgree={isReadAllContent}
          agreementsList={agreementsList}
          agreementsListValues={agreementsListValues}
          handleChangeAgreements={handleChangeAgreements}
        />
      )}
      {agreements ? (
        getButtons()
      ) : (
        <React.Fragment>
          {handleLater && (
            <button onClick={handleLater} className="button button--secondary">
              <FormattedMessageHelper tagName="span" id="agreements.later" defaultMessage={localeData.en['agreements.later']} />
            </button>
          )}
        </React.Fragment>
      )}
    </React.Fragment>
  );
};
